import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Drawer, IconButton, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import NoDataFound from '../../../../components/NodataIndication/NoDataFound';
import { FONT_SERIF, WD } from '../../../warningDashboard/warningTheme';
import { CARD_BG, PAGE_BG } from '../utils/constants';
import { addDays, daysBetween, describeRange, startOfWeek } from '../utils/dates';
import { personWeek } from '../utils/hours';
import { sortForDrawer } from '../utils/tasks';
import PersonAvatar from './PersonAvatar';
import LoadTag from './LoadTag';
import PersonSummary from './PersonSummary';
import WeekChart from './WeekChart';
import TaskItem from './TaskItem';

/**
 * One person's hours, opened from the table or the calendar. Also where hours are written: a head sets planned hours per task, and
 * the person (or a head above them) logs from -> to time against a plan. The server decides who may do either; `row.canPlan` and
 * `canLog` only decide which controls are offered.
 */
export default function PersonDrawer({ row, idx, from, to, today, projectId, canLog, onChanged, onClose }) {
  const [expanded, setExpanded] = useState(null);
  useEffect(() => setExpanded(null), [row?.person.id]);

  const week = useMemo(() => {
    if (!row) return [];
    const monday = startOfWeek(from);
    return personWeek(idx, row.person.id, daysBetween(monday, addDays(monday, 6)), projectId);
  }, [row, idx, from, projectId]);

  const items = useMemo(() => (row ? sortForDrawer(row.items) : []), [row]);

  return (
    <Drawer anchor="right" open={Boolean(row)} onClose={onClose} PaperProps={{ sx: { width: '100%', maxWidth: 540, bgcolor: PAGE_BG, border: 'none' } }}>
      {row && (
        <Box display="flex" flexDirection="column" sx={{ height: '100%' }} role="region" aria-label={`${row.person.name}'s workload`}>
          <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, sm: 3 } }}>
            {/* Who */}
            <Box display="flex" alignItems="flex-start" gap={1.75}>
              <PersonAvatar person={row.person} size={52} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
                  <Typography component="h2" noWrap sx={{ fontFamily: FONT_SERIF, fontSize: 21, fontWeight: 600, color: WD.ink }}>
                    {row.person.name}
                  </Typography>
                  <LoadTag level={row.level} small />
                </Box>
                <Typography sx={{ mt: 0.25, fontSize: 12.5, color: WD.inkMuted }}>{[row.person.title, describeRange(from, to)].filter(Boolean).join(' · ')}</Typography>
              </Box>
              <IconButton aria-label="Close" onClick={onClose} autoFocus sx={{ bgcolor: '#EFEDE6' }}>
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Box>

            <PersonSummary row={row} />
            <WeekChart week={week} today={today} capacityPerDay={idx.capacityPerDay} />

            {/* Tasks */}
            <Box sx={{ mt: 3.5 }}>
              <Typography component="h3" sx={{ fontSize: 14, fontWeight: 700, color: WD.ink }}>Tasks ({items.length})</Typography>
              {items.length ? (
                <Box sx={{ mt: 1, bgcolor: CARD_BG, border: `1px solid ${WD.border}`, borderRadius: 3, px: 2 }}>
                  {items.map((item, index) => (
                    <TaskItem
                      key={item.task.taskId}
                      item={item}
                      row={row}
                      idx={idx}
                      today={today}
                      canLog={canLog}
                      last={index === items.length - 1}
                      open={expanded === item.task.taskId}
                      onToggle={() => setExpanded((was) => (was === item.task.taskId ? null : item.task.taskId))}
                      onChanged={onChanged}
                    />
                  ))}
                </Box>
              ) : (
                <NoDataFound message="No tasks in these dates." />
              )}
            </Box>
          </Box>

          <Box display="flex" justifyContent="flex-end" sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: `1px solid ${WD.border}`, bgcolor: PAGE_BG }}>
            <Button variant="outlined" onClick={onClose}>Close</Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
